
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { linkTwitterHandle } from '@/lib/engagement/twitter';
import { toast } from "sonner";

interface TwitterHandleLinkingProps {
  currentTwitterHandle?: string;
  onHandleLinked: (handle: string) => void;
}

const TwitterHandleLinking = ({ currentTwitterHandle, onHandleLinked }: TwitterHandleLinkingProps) => {
  const [twitterHandle, setTwitterHandle] = useState(currentTwitterHandle || '');
  const [isLinking, setIsLinking] = useState(false);

  const handleLinkTwitter = async () => {
    if (!twitterHandle.trim()) {
      toast.error("Please enter your Twitter handle");
      return;
    }

    // Clean the handle (remove @ if present)
    const cleanHandle = twitterHandle.replace('@', '').trim();
    
    if (cleanHandle.length < 2) {
      toast.error("Please enter a valid Twitter handle");
      return;
    }

    setIsLinking(true);
    
    try {
      const result = await linkTwitterHandle(cleanHandle);

      if (result.success) {
        toast.success("Twitter handle linked successfully!");
        onHandleLinked(cleanHandle);
      } else {
        toast.error(result.error || "Failed to link Twitter handle");
      }
    } catch (error) {
      console.error('Error linking Twitter handle:', error);
      toast.error("Failed to link Twitter handle");
    }
    
    setIsLinking(false);
  };

  return (
    <div className="bg-navy-dark p-4 rounded-lg border border-tan-dark">
      <h3 className="text-lg font-bold text-tan mb-3">Link Your Twitter</h3>
      <p className="text-sm text-tan/80 mb-3">
        Link your Twitter handle to automatically track when you share your BroCode and earn +10 BroScore!
      </p>
      
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan/60">@</span>
          <Input
            type="text"
            placeholder="your_twitter_handle"
            value={twitterHandle}
            onChange={(e) => setTwitterHandle(e.target.value)}
            className="pl-8 bg-navy border-tan-dark text-tan placeholder-tan/40"
          />
        </div>
        <Button
          onClick={handleLinkTwitter}
          disabled={isLinking || !twitterHandle.trim()}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          {isLinking ? 'Linking...' : 'Link'}
        </Button>
      </div>
      
      {currentTwitterHandle && (
        <p className="text-xs text-green-400 mt-2">
          ✓ Linked: @{currentTwitterHandle}
        </p>
      )}
    </div>
  );
};

export default TwitterHandleLinking;
